import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Target, Building, User, DollarSign, Calendar, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import apiService from '../services/api';

const DealProfilePage = ({ selectedLocation }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDeal();
  }, [id]);

  const loadDeal = async () => {
    if (!id) {
      setError('No deal ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const deals = await apiService.getDeals();
      const dealData = deals.find(d => d.id === id);
      
      if (dealData) {
        setDeal(dealData);
      } else {
        setError('Deal not found');
      }
    } catch (error) {
      console.error('Error loading deal:', error);
      setError('Deal not found');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/crm/deals');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStageDisplayName = (stage) => {
    return stage?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const getStageColor = (stage) => {
    const colors = {
      'form_submitted': 'bg-blue-100 text-blue-800',
      'tour_booked': 'bg-yellow-100 text-yellow-800',
      'tour_completed': 'bg-orange-100 text-orange-800',
      'agreement_sent': 'bg-purple-100 text-purple-800',
      'agreement_signed': 'bg-green-100 text-green-800',
      'close_lost': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading deal...</p>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Deal not found'}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Deals
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Deals
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Deal Profile</h1>
            <p className="text-sm text-gray-600">View and manage deal information</p>
          </div>
        </div>
      </div>

      {/* Deal Content */}
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Deal Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{deal.deal_name}</h2>
                  <Badge className={getStageColor(deal.stage)}>
                    {getStageDisplayName(deal.stage)}
                  </Badge>
                </div>
                
                <p className="text-gray-600 mb-2">{deal.company_name}</p>
                
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    <span className="font-medium">{formatCurrency(deal.deal_value || 0)}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    <span>{deal.contact?.name || 'No Contact'}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{deal.location_name || 'No Location'}</span>
                  </div>
                  
                  {deal.term_months && (
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{deal.term_months} months</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Deal Details */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="contact">Contact & Company</TabsTrigger>
              <TabsTrigger value="terms">Terms & Pricing</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Deal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Deal Name</p>
                      <p className="font-medium">{deal.deal_name}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Stage</p>
                      <Badge className={getStageColor(deal.stage)}>
                        {getStageDisplayName(deal.stage)}
                      </Badge>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Probability</p>
                      <p className="font-medium">{deal.probability || 0}%</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Agreement Type</p>
                      <p className="font-medium">{deal.agreement_type || 'Not specified'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Membership Type</p>
                      <p className="font-medium">{deal.membership_type || 'Not specified'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Location & Timing</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="font-medium">{deal.location_name || 'Not specified'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Contract Location</p>
                      <p className="font-medium">{deal.contract_location || 'Not specified'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Start Date</p>
                      <p className="font-medium">{formatDate(deal.start_date)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Created</p>
                      <p className="font-medium">{formatDate(deal.created_at)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Last Updated</p>
                      <p className="font-medium">{formatDate(deal.updated_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {deal.contact ? (
                      <>
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="font-medium">{deal.contact.name}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium">{deal.contact.email}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">{deal.contact.phone || 'Not provided'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">Title</p>
                          <p className="font-medium">{deal.contact.title || 'Not provided'}</p>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">No contact information available</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building className="w-5 h-5 mr-2" />
                      Company Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Company Name</p>
                      <p className="font-medium">{deal.company_name}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Deal Value</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(deal.deal_value || 0)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="terms" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Pricing Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Offered Price</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(deal.offered_price || 0)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Market Rate</p>
                      <p className="font-medium">{formatCurrency(deal.market_rate || 0)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Total Deal Value</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(deal.deal_value || 0)}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Terms</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Term Length</p>
                      <p className="font-medium">{deal.term_months || 0} months</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Start Date</p>
                      <p className="font-medium">{formatDate(deal.start_date)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Monthly Rate</p>
                      <p className="font-medium">{formatCurrency(deal.offered_price || 0)} / month</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default DealProfilePage;